import moment from 'moment';
import { APPRAISER_EVENT_STAGE } from '../constants/AppraiserEventStage';
import { formatDate, toHumanReadableDate } from '@shared/services/dateService';
import { AtlPreferencesMapper } from './AtlPreferencesMapper';
import { RegionsMapper } from './RegionsMapper';
import { EVENT_APPRAISER_ASSIGNMENT_ROLE } from '../constants/EventAppraiserAssignmentRole';
import { AtlSponsorRankingMapper } from './AtlSponsorRankingMapper';

const CalendarEventMapper = {
  toCalendarEventData: events =>
    events.map(item => ({
      ...item,
      IsAtlAssignedStage:
        item.Stage >= APPRAISER_EVENT_STAGE.ATL_ASSIGNED &&
        item.Stage <= APPRAISER_EVENT_STAGE.ATL_ASSIGNED_SUBMITTED
    })),
  toApolloAddEventInput: event => {
    return {
      Type: event.eventType,
      Start: event.startDate,
      End: event.endDate,
      OrgId: event.orgId,
      OrgUnitIds: event.orgUnitIds,
      Domains: event.domains,
      AtlExternalUserId: event.atl
    };
  },
  toApolloEditEventInput: event => {
    return {
      Id: event.eventId,
      Start: formatDate(event.startDate),
      End: formatDate(event.endDate),
      OrgUnitIds: event.orgUnitIds,
      Domains: event.domains,
      Regions: RegionsMapper.toApolloInput(event.regions),
      AtlExternalUserId: event.atl ? event.atl.value : null,
      Teams: event.eventTeams.map(x => ({
        Role: x.role,
        NumberOfPeople: x.numberOfPeople,
        Notes: x.notes
      })),
      LogisticalNotes: event.logisticalNotes,
      DeliveryMethod: event.deliveryMethod,
      IntendedStartDate: event.intendedStartDate ? formatDate(event.intendedStartDate) : null,
      IntendedEndDate: event.intendedStartDate ? formatDate(event.intendedEndDate) : null,
      AtlPreferences: event.atlPreferences?.map(AtlPreferencesMapper.toApolloInput) || null,
      EventAppraiserAssignments: event.eventAssignments?.map(x => ({
        Id: x.id,
        ExternalUserId: x.externalUserId,
        Rank: x.assignmentRank ? x.assignmentRank : null,
        ActiveRole: x.role
      })),
      SowStatuses: event.sowStatuses?.map(status => ({
        Id: status.id,
        ContractTypes: status.contractTypes,
        ContractStatus: status.contractStatus,
        ContractDate: status.contractDate ? formatDate(status.contractDate) : null,
        StatusType: status.statusType
      })),
      AtlObservationAssignment: event.atlObserver
        ? {
            ObserverExternalUserId: event.atlObserver.value
          }
        : null,
      AtmObservationAssignments:
        event.atmObservationAssignments?.map(obs => ({
          ObserverExternalUserId: obs.observerExternalUserId,
          ObservedExternalUserId: obs.observedExternalUserId
        })) || null
    };
  },
  eventDataToEditFields: event => {
    return {
      eventId: event.Id,
      eventType: event.Type,
      startDate: moment(event.StartDate).toDate(),
      endDate: moment(event.EndDate).toDate(),
      intendedStartDate: event.IntendedStartDate ? moment(event.IntendedStartDate).toDate() : null,
      intendedEndDate: event.IntendedEndDate ? moment(event.IntendedEndDate).toDate() : null,
      orgUnitIds: event.OrgUnitIds,
      domains: event.Domains,
      regions: RegionsMapper.toEditFields(event.Regions),
      atl: event.Atl
        ? {
            value: event.Atl.ExternalUserId,
            label: `${event.Atl.Name} - ${event.Atl.CurrentRole.Title}`,
            currentRole: event.Atl.CurrentRole.Value
          }
        : null,
      atlObserver: event.AtlObserver
        ? {
            value: event.AtlObserver.ExternalUserId,
            label: `${event.AtlObserver.Name} - ${event.AtlObserver.CurrentRole.Title}`,
            currentRole: event.AtlObserver.CurrentRole.Value
          }
        : null,
      logisticalNotes: event.LogisticalNotes,
      deliveryMethod: event.DeliveryMethod,
      eventTeams: event.Teams.map(x => ({
        role: x.Role,
        numberOfPeople: x.NumberOfPeople,
        notes: x.Notes
      })),
      atlPreferences: event?.AtlPreferences?.map(AtlPreferencesMapper.toAtlPreferencesFormData),
      atlSponsorRankings: event?.AtlSponsorRankings?.map(
        AtlSponsorRankingMapper.toAtlSponsorRankingFormData
      ),
      eventAssignments: CalendarEventMapper.toEditAssignments(event.EventAssignments),
      atmObservationAssignments: CalendarEventMapper.toEditAtmObservationAssignments(
        event.AtmObservationAssignments
      ),
      sowStatuses: event.SowStatuses?.map(status => ({
        id: status.Id,
        atmType: status.AtmInfo?.TypeDisplayName,
        atmName: status.AtmInfo?.Name,
        contractTypes: status.ContractTypes,
        contractStatus: status.ContractStatus,
        contractDate: status.ContractDate ? new Date(status.ContractDate) : null,
        statusType: status.StatusType
      }))
    };
  },
  toApolloChangeDateRequestInput: (requestData, eventId) => ({
    EventId: eventId,
    StartDate: formatDate(requestData.startDate),
    EndDate: formatDate(requestData.endDate),
    SubmitComment: requestData.comment
  }),
  toApolloReviewChangeDateRequestInput: (id, eventId, status, declineComment) => ({
    Id: id,
    EventId: eventId,
    Status: status,
    DeclineComment: declineComment
  }),
  toOpenForATLBidEventsTable: events =>
    events.map(event => ({
      id: event.Id,
      startDate: event.StartDate,
      orgUnitNames: event.OrgUnitNames,
      eventBidDate: event.EventBid?.BidDate
    })),
  toEditAssignments: eventAssignments =>
    eventAssignments?.map(x => ({
      id: x.EventAppraiserAssignment?.Id,
      externalUserId: x.Appraiser.ExternalUserId,
      willingToTravelToAfrica: x.Appraiser.WillingToTravelToAfrica ?? false,
      willingToTravelToAsia: x.Appraiser.WillingToTravelToAsia ?? false,
      willingToTravelToAustralia: x.Appraiser.WillingToTravelToAustralia ?? false,
      willingToTravelToEurope: x.Appraiser.WillingToTravelToEurope ?? false,
      willingToTravelToNorthAmerica: x.Appraiser.WillingToTravelToNorthAmerica ?? false,
      willingToTravelToSouthAmerica: x.Appraiser.WillingToTravelToSouthAmerica ?? false,
      willingToTravelToVirtual: x.Appraiser.WillingToTravelToVirtual ?? false,
      name: x.Appraiser.Name,
      numOfEventsBeforeAfter: x.NumOfEventsBeforeAfter,
      eventBidDate: x.EventBid ? toHumanReadableDate(x.EventBid.BidDate) : null,
      assignmentRank: x.EventAppraiserAssignment?.Rank ?? null,
      role: x.EventAppraiserAssignment?.ActiveRole || EVENT_APPRAISER_ASSIGNMENT_ROLE.NA,
      status: x.EventAppraiserAssignment?.ActiveStatus,
      type: x.Type,
      teamRole: x.TeamRole,
      currentRole: x.AssignmentCurrentRole,
      currentRoleName: x.AssignmentCurrentRoleName,
      canBeObserved: x.CanBeObserved,
      canObserve: x.CanObserve,
      isUnavailableAfterDCR: x.IsUnavailableAfterDCR,
      currentUserAssignmentPermissions: {
        canEditRole: x.CurrentUserAssignmentPermissions.CanEditRole,
        canEditRank: x.CurrentUserAssignmentPermissions.CanEditRank
      }
    })),
  toEditAtmObservationAssignments: atmObservationAssignments =>
    atmObservationAssignments?.map(obs => ({
      observerExternalUserId: obs.ObserverExternalUserId,
      observedExternalUserId: obs.ObservedExternalUserId
    }))
};

export default CalendarEventMapper;
